// @noflow
import * as deviceIcons from '~/core/constants/icons/device';

import { bulbTypes, deviceModels, deviceTypes, plugTypes, deviceModes } from '~/core/constants/devices';
import { createComponentId } from '~/core/utils/schedule';
import { getCurrentScheduleEvent } from '~/core/state/ui/schedule/selectors';
import svgDeviceIcons from '~/core/constants/icons/devices-svg';

const EU = 'EU';
const NA = 'NA';
const UK = 'UK';
const HEATING_OFF_TARGET = 1;
const DUAL = 'dual';
const HEAT = 'heat';
const COOL = 'cool';
const capabilityTypes = [DUAL, HEAT, COOL];
const MODE_OFF = 'OFF';
const MODE_AUTO = 'AUTO';
const MODE_HEAT = 'HEAT';
const MODE_COOL = 'COOL';

// Icons where models with the same device type have different icons
const deviceModelToIcon = {
    [deviceModels.NANO1]: deviceIcons.OLD_HUB,
    [deviceModels.NANO2]: deviceIcons.NEW_HUB,
    [deviceModels.SLR1]: deviceIcons.RECEIVER_SINGLE,
    [deviceModels.SLR2]: deviceIcons.RECEIVER_DUAL,
    [deviceModels.SLT1]: deviceIcons.STAT1,
    [deviceModels.SLT2]: deviceIcons.STAT2,
    [deviceModels.SLT3]: deviceIcons.STAT3,
    [deviceModels.SLT3b]: deviceIcons.STAT3,
    [deviceModels.SLT4]: deviceIcons.STAT3
};

const lightIconsMap = {
    true: {
        [UK]: deviceIcons.LIGHT_ON
    },
    false: {
        [UK]: deviceIcons.LIGHT_OFF
    }
};

const deviceIconActiveMap = {
    [deviceTypes.ACTIVE_PLUG]: {
        true: {
            [EU]: deviceIcons.SMART_PLUG_OFF_EU,
            [NA]: deviceIcons.SMART_PLUG_OFF_NA,
            [UK]: deviceIcons.SMART_PLUG_ON
        },
        false: {
            [EU]: deviceIcons.SMART_PLUG_OFF_EU,
            [NA]: deviceIcons.SMART_PLUG_OFF_NA,
            [UK]: deviceIcons.SMART_PLUG_OFF
        }
    },
    [deviceTypes.BOILER_MODULE]: {
        false: {
            [UK]: deviceIcons.RECEIVER_DUAL
        }
    },
    [deviceTypes.NA_THERMOSTAT]: {
        true: {
            [UK]: deviceIcons.STAT3,
            [NA]: deviceIcons.STAT3
        },
        false: {
            [UK]: deviceIcons.STAT3,
            [NA]: deviceIcons.STAT3
        }
    },
    [deviceTypes.COLOR_TUNEABLE_LIGHT]: lightIconsMap,
    [deviceTypes.COLOR_LIGHT]: lightIconsMap,
    [deviceTypes.CONTACT_SENSOR]: {
        true: {
            [UK]: deviceIcons.CONTACT_SENSOR_OPEN
        },
        false: {
            [UK]: deviceIcons.CONTACT_SENSOR_CLOSED
        }
    },
    [deviceTypes.HEATING]: {
        true: {
            [UK]: deviceIcons.HEATING_ON
        },
        false: {
            [UK]: deviceIcons.HEATING_OFF
        }
    },
    [deviceTypes.HOT_WATER]: {
        true: {
            [UK]: deviceIcons.HOT_WATER_ON
        },
        false: {
            [UK]: deviceIcons.HOT_WATER_OFF
        }
    },
    [deviceTypes.HUB]: {
        false: {
            [UK]: deviceIcons.NEW_HUB
        }
    },
    [deviceTypes.MOTION_SENSOR]: {
        true: {
            [UK]: deviceIcons.MOTION_SENSOR_ON
        },
        false: {
            [UK]: deviceIcons.MOTION_SENSOR_OFF
        }
    },
    [deviceTypes.SIGNAL_BOOSTER]: {
        true: {
            [UK]: deviceIcons.SIGNAL_BOOSTER_ON
        },
        false: {
            [UK]: deviceIcons.SIGNAL_BOOSTER_OFF
        }
    },
    [deviceTypes.SMART_PLUG]: {
        true: {
            [EU]: deviceIcons.SMART_PLUG_ON_EU,
            [NA]: deviceIcons.SMART_PLUG_ON_NA,
            [UK]: deviceIcons.SMART_PLUG_ON
        },
        false: {
            [EU]: deviceIcons.SMART_PLUG_OFF_EU,
            [NA]: deviceIcons.SMART_PLUG_OFF_NA,
            [UK]: deviceIcons.SMART_PLUG_OFF
        }
    },
    [deviceTypes.THERMOSTAT_UI]: {
        true: {
            [UK]: deviceIcons.STAT3
        },
        false: {
            [UK]: deviceIcons.STAT3
        }
    },
    [deviceTypes.TUNEABLE_LIGHT]: lightIconsMap,
    [deviceTypes.WARM_WHITE_LIGHT]: lightIconsMap,
    [deviceTypes.LEAK_SENSOR]: {
        true: {
            [UK]: deviceIcons.LEAK_SENSOR_ON
        },
        false: {
            [UK]: deviceIcons.LEAK_SENSOR_OFF
        }
    },
    [deviceTypes.HIVE_BUTTON]: {
        true: {
            [UK]: deviceIcons.BUTTON_ON
        },
        false: {
            [UK]: deviceIcons.BUTTON_OFF
        }
    }
};

export const iconForDevice = ({ type, model, active = false, localeSuffix = UK }) => {
    return (
        deviceModelToIcon[model] ||
        (deviceIconActiveMap[type]
            ? deviceIconActiveMap[type][active][localeSuffix] || deviceIconActiveMap[type][active][UK]
            : undefined)
    );
};

const getIsDualModeActive = product => {
    const currentMode = product.getIn(['state', 'controlMode'], 'OFF');
    const currentHeat = product.getIn(['state', 'heat'], 0);
    const currentCool = product.getIn(['state', 'cool'], 0);
    const currentTemperature = product.getIn(['props', 'temperature'], 0);
    const truthMap = {
        [currentMode === MODE_AUTO]: currentCool < currentTemperature || currentHeat > currentTemperature,
        [currentMode === MODE_HEAT]: currentHeat > currentTemperature,
        [currentMode === MODE_COOL]: currentCool < currentTemperature
    };
    return truthMap[true];
};

export function isProductActive(product, state) {
    if (!product.getIn(['props', 'online']) || product.getIn(['props', 'error'])) {
        return false;
    }

    const type = product.get('type');

    if (type === deviceTypes.LEAK_SENSOR) {
        return product.getIn(['props', 'status']) === 'ON';
    }

    if (type === deviceTypes.CONTACT_SENSOR) {
        return product.getIn(['props', 'status']) === 'OPEN';
    }

    if (type === deviceTypes.HEATING) {
        const componentId = createComponentId(deviceTypes.HEATING, product.get('id'));
        const currentSchedule = getCurrentScheduleEvent(state, {
            componentId,
            saved: true
        });
        const inside = product.getIn(['props', 'temperature']);
        const target = getHeatingTarget(product, currentSchedule);
        return target > inside;
    }

    if (type === deviceTypes.HOT_WATER) {
        const componentId = createComponentId(deviceTypes.HOT_WATER, product.get('id'));
        const currentSchedule = getCurrentScheduleEvent(state, {
            componentId,
            saved: true
        });
        return getHotwaterStatus(product, currentSchedule) === 'ON';
    }

    if (type === deviceTypes.NA_THERMOSTAT) {
        if (!product.getIn(['state', 'status'], false)) {
            return false;
        }
        const statType = product.getIn(['props', 'capabilities']).find(c => capabilityTypes.includes(c));

        const currentHeat = product.getIn(['state', 'heat'], 0);
        const currentCool = product.getIn(['state', 'cool'], 0);
        const currentTemperature = product.getIn(['props', 'temperature'], 0);

        const truthMap = {
            [statType === COOL]: currentCool < currentTemperature,
            [statType === HEAT]: currentHeat > currentTemperature,
            [statType === DUAL]: getIsDualModeActive(product)
        };
        return truthMap[true];
    }

    if (bulbTypes.includes(type)) {
        return product.getIn(['state', 'status']) === 'ON';
    }

    if (type === deviceTypes.MOTION_SENSOR) {
        return product.getIn(['props', 'motion', 'status']);
    }

    if (plugTypes.includes(type)) {
        return product.getIn(['state', 'status']) === 'ON';
    }

    return false;
}

export function getHeatingTarget(product, currentSchedule) {
    const mode = product.getIn(['state', 'mode']);
    const frostProtection = product.getIn(['state', 'frostProtection'], 7);
    const scheduleOverride = product.getIn(['props', 'scheduleOverride']);
    const scheduleTarget = currentSchedule.getIn(['value', 'target']);
    const target = product.getIn(['state', 'target']);
    const calculatedTarget =
        mode === 'SCHEDULE' && !scheduleOverride ? scheduleTarget : mode === deviceModes.OFF ? frostProtection : target;

    return calculatedTarget === HEATING_OFF_TARGET ? frostProtection : calculatedTarget;
}

export function getHotwaterStatus(product, currentSchedule) {
    const mode = product.getIn(['state', 'mode']);
    if (mode === 'SCHEDULE') {
        return currentSchedule.getIn(['value', 'status'], 'OFF');
    } else {
        return mode !== 'OFF' ? 'ON' : 'OFF';
    }
}

const bulbToUrl = p => `/products/bulb/${p.get('id')}`;

// Sometimes we want urls that don't match 1-1 with the product type
// Examples include our 3 bulbs, and thermostats where
// we want /bulb/ and /thermostat/ respectively.
const customUrlFormatters = {
    [deviceTypes.COLOR_TUNEABLE_LIGHT]: bulbToUrl,
    [deviceTypes.COLOR_LIGHT]: bulbToUrl,
    [deviceTypes.TUNEABLE_LIGHT]: bulbToUrl,
    [deviceTypes.WARM_WHITE_LIGHT]: bulbToUrl,
    [deviceTypes.NA_THERMOSTAT]: p => `/products/thermostat/${p.get('id')}`,
    default: p => `/products/${p.get('type')}/${p.get('id')}`
};

export function makeProductLink(product) {
    const type = product && product.get('type');
    const formatter = customUrlFormatters[type] || customUrlFormatters.default;
    return formatter(product);
}

const getDualModeIcons = product => {
    const currentMode = product.getIn(['state', 'controlMode'], 'OFF');
    const truthMap = {
        [currentMode === MODE_OFF]: [],
        [currentMode === MODE_AUTO]: [svgDeviceIcons.COOLING, svgDeviceIcons.HEATING],
        [currentMode === MODE_HEAT]: [svgDeviceIcons.HEATING],
        [currentMode === MODE_COOL]: [svgDeviceIcons.COOLING]
    };
    return truthMap[true];
};

export const getNAIcons = product => {
    if (!product.getIn(['state', 'status'], false)) {
        return [];
    }

    const statType = product.getIn(['props', 'capabilities']).find(c => capabilityTypes.includes(c));

    const truthMap = {
        [statType === DUAL]: getDualModeIcons(product),
        [statType === HEAT]: [svgDeviceIcons.HEATING],
        [statType === COOL]: [svgDeviceIcons.COOLING]
    };
    return truthMap[true];
};

export const isSensorLeaking = product => ['LOW', 'HIGH'].includes(product.getIn(['props', 'leaking']));



// WEBPACK FOOTER //
// ./src/core/utils/devices.js